/**
 * Fechas — una sola fuente.
 *
 * Dos formatos y ni uno más:
 *   presentación   `dd/MM/yyyy` y `dd/MM/yyyy HH:mm`
 *   DGII           `dd-MM-yyyy` y `dd-MM-yyyy HH:mm:ss`  (docs/00, §6)
 *
 * Internamente el backend maneja `DateTimeOffset` en UTC. Aquí se presenta en
 * la zona de República Dominicana, que es fija (UTC-4, sin horario de verano),
 * salvo cuando el valor es una fecha de calendario `yyyy-MM-dd`: esa no tiene
 * hora y convertirla de zona la correría un día.
 */

export const ZONA_RD = 'America/Santo_Domingo'

const RE_SOLO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/

export interface PartesCalendario {
  anio: number
  mes: number // 1-12
  dia: number
  hora: number
  minuto: number
  segundo: number
}

export type EntradaFecha = Date | string | number | null | undefined

/** Descompone cualquier entrada en partes de calendario dominicano. */
export function partesCalendario(valor: EntradaFecha): PartesCalendario | null {
  if (valor === null || valor === undefined || valor === '') return null

  if (typeof valor === 'string') {
    const soloFecha = RE_SOLO_FECHA.exec(valor)
    if (soloFecha) {
      return {
        anio: Number(soloFecha[1]),
        mes: Number(soloFecha[2]),
        dia: Number(soloFecha[3]),
        hora: 0,
        minuto: 0,
        segundo: 0,
      }
    }
  }

  const fecha = valor instanceof Date ? valor : new Date(valor)
  if (Number.isNaN(fecha.getTime())) return null

  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_RD,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(fecha)

  const leer = (tipo: Intl.DateTimeFormatPartTypes): number => {
    const parte = partes.find((p) => p.type === tipo)
    return parte ? Number(parte.value) : 0
  }

  // `hour: '2-digit'` con hour12 false puede devolver 24 en la medianoche.
  const hora = leer('hour') % 24

  return {
    anio: leer('year'),
    mes: leer('month'),
    dia: leer('day'),
    hora,
    minuto: leer('minute'),
    segundo: leer('second'),
  }
}

const dos = (n: number): string => String(n).padStart(2, '0')

/** `2026-09-21` → `"21/09/2026"`. */
export function formatearFecha(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return '—'
  return `${dos(p.dia)}/${dos(p.mes)}/${p.anio}`
}

/** `"21/09/2026 14:32"`. */
export function formatearFechaHora(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return '—'
  return `${dos(p.dia)}/${dos(p.mes)}/${p.anio} ${dos(p.hora)}:${dos(p.minuto)}`
}

/** `"14:32:07"` — la línea de tiempo del documento usa segundos. */
export function formatearHora(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return '—'
  return `${dos(p.hora)}:${dos(p.minuto)}:${dos(p.segundo)}`
}

/** Formato que espera DGII: `dd-MM-yyyy`. Lo que viaja, no lo que se ve. */
export function formatearFechaDGII(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return ''
  return `${dos(p.dia)}-${dos(p.mes)}-${p.anio}`
}

/** Formato que espera DGII con hora: `dd-MM-yyyy HH:mm:ss`. */
export function formatearFechaHoraDGII(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return ''
  return `${dos(p.dia)}-${dos(p.mes)}-${p.anio} ${dos(p.hora)}:${dos(p.minuto)}:${dos(p.segundo)}`
}

/** `yyyy-MM-dd` — lo que se pone en un `<input type="date">` y en la URL. */
export function aFechaISO(valor: EntradaFecha): string {
  const p = partesCalendario(valor)
  if (!p) return ''
  return `${p.anio}-${dos(p.mes)}-${dos(p.dia)}`
}

/* ---------------------------------------------------------------------- */
/* El corte del 15                                                         */
/* ---------------------------------------------------------------------- */

/**
 * Fecha límite para manifestar conformidad sobre un comprobante recibido:
 * el **día 15 del mes siguiente** a la fecha de la factura (docs/00, §3.4).
 *
 * Devuelve la fecha de calendario del corte en `yyyy-MM-dd`.
 */
export function corteDelQuince(fechaFactura: EntradaFecha): string {
  const p = partesCalendario(fechaFactura)
  if (!p) return ''
  const mesSiguiente = p.mes === 12 ? 1 : p.mes + 1
  const anio = p.mes === 12 ? p.anio + 1 : p.anio
  return `${anio}-${dos(mesSiguiente)}-15`
}

/**
 * Días de calendario que faltan para el corte de esa factura.
 * Negativo cuando el corte ya pasó. Cero el mismo día 15.
 */
export function diasHastaCorte(fechaFactura: EntradaFecha, hoy: EntradaFecha = new Date()): number | null {
  const corte = corteDelQuince(fechaFactura)
  if (corte === '') return null
  const hoyISO = aFechaISO(hoy)
  if (hoyISO === '') return null
  return diasEntre(hoyISO, corte)
}

/** Días de calendario entre dos fechas, `hasta - desde`. */
export function diasEntre(desde: EntradaFecha, hasta: EntradaFecha): number | null {
  const a = partesCalendario(desde)
  const b = partesCalendario(hasta)
  if (!a || !b) return null
  const msA = Date.UTC(a.anio, a.mes - 1, a.dia)
  const msB = Date.UTC(b.anio, b.mes - 1, b.dia)
  return Math.round((msB - msA) / 86_400_000)
}

export type EstadoCorte = 'holgado' | 'proximo' | 'critico' | 'fuera-de-ventana'

/**
 * Semáforo del corte del 15.
 *
 * El documento fija verde sobre 10 días, ámbar bajo 5, rojo el 14 y el 15
 * (1 y 0 días), y gris fuera de ventana. La banda de 5 a 10 días no está
 * escrita; se resuelve en ámbar porque quedarse corto de aviso cuesta más
 * que avisar de más.
 */
export function estadoDeCorte(dias: number | null): EstadoCorte {
  if (dias === null) return 'fuera-de-ventana'
  if (dias < 0) return 'fuera-de-ventana'
  if (dias <= 1) return 'critico'
  if (dias <= 10) return 'proximo'
  return 'holgado'
}

/* ---------------------------------------------------------------------- */
/* Contadores de plazo                                                     */
/* ---------------------------------------------------------------------- */

/**
 * "lleva 9 h de 72" — el contador de pendientes es también un contador de
 * tiempo. Todo contador del producto dice cuánto tiempo queda, nunca solo
 * cuántos hay.
 */
export function formatearAntiguedad(desde: EntradaFecha, ahora: EntradaFecha = new Date()): string {
  const a = desde instanceof Date ? desde : desde ? new Date(desde) : null
  const b = ahora instanceof Date ? ahora : new Date(ahora as string | number)
  if (!a || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '—'

  const minutos = Math.max(0, Math.floor((b.getTime() - a.getTime()) / 60_000))
  if (minutos < 60) return `${minutos} min`
  const horas = Math.floor(minutos / 60)
  if (horas < 48) return `${horas} h`
  return `${Math.floor(horas / 24)} d`
}
